// src/components/titlebar/components/ServerSearchDialog.tsx

import React from "react"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
} from "@mui/material"
import { Storage as ServerIcon, Refresh as RefreshIcon } from "@mui/icons-material"
import type { ServerSearchDialogProps } from "../types/titleBarTypes"

const ServerSearchDialog: React.FC<ServerSearchDialogProps> = ({ open, isSearching, searchResults, onClose, onSearch, onConnectToServer }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backdropFilter: "blur(20px)",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ServerIcon color="primary" />
          <Typography variant="h6">서버 검색 결과</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isSearching ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={3}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography color="text.secondary">네트워크에서 서버를 검색하고 있습니다...</Typography>
          </Box>
        ) : searchResults.length > 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              발견된 서버 목록입니다. 연결할 서버를 선택하세요.
            </Typography>
            <List sx={{ maxHeight: 300, overflow: "auto" }}>
              {searchResults.map((server, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton
                    onClick={() => onConnectToServer(server)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "success.main",
                        mr: 2,
                        flexShrink: 0,
                      }}
                    />
                    <ListItemText
                      primary={server}
                      secondary={`서버 IP: ${server}`}
                      primaryTypographyProps={{
                        fontWeight: 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={3}>
            <ServerIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" mb={1}>
              발견된 서버가 없습니다
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              네트워크에서 접근 가능한 서버를 찾을 수 없습니다.
              <br />
              네트워크 연결을 확인하고 다시 시도해보세요.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!isSearching && (
          <>
            <Button onClick={onSearch} startIcon={<RefreshIcon />} disabled={isSearching}>
              다시 검색
            </Button>
            <Button onClick={onClose}>닫기</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ServerSearchDialog
